import React, { useState, useEffect, useCallback } from 'react';
import { Database, ChevronLeft, ChevronRight, Eye, AlertCircle } from 'lucide-react';
import duckdbService from '../services/duckdbService';

const DataViewer = ({ selectedFile, onFileSelect }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(50);

  const fetchData = useCallback(async () => {
    if (!selectedFile) return;

    setLoading(true);
    setError(null);

    try {
      console.log('Fetching data for file:', selectedFile);
      const result = await duckdbService.viewData(
        selectedFile.file_id,
        pageSize,
        currentPage * pageSize
      );
      console.log('Data fetched successfully:', result);
      setData(result);
    } catch (error) {
      console.error('Data fetch error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [selectedFile, currentPage, pageSize]);

  useEffect(() => {
    if (selectedFile) {
      fetchData();
    } else {
      setData(null);
      setError(null);
    }
  }, [selectedFile, currentPage, fetchData]);

  if (!selectedFile) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-16 h-16 bg-sand-100 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-[rgba(32,30,29,0.12)]">
            <Database className="w-8 h-8 text-sand-500" />
          </div>
          <h3 className="text-xl font-semibold text-ink mb-2">No File Selected</h3>
          <p className="text-sand-700">Please upload or select a file to view data</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="card border-red-200 bg-red-50">
          <div className="flex items-center gap-3 text-red-600 mb-4">
            <AlertCircle className="w-6 h-6" />
            <h2 className="text-xl font-semibold">Error Loading Data</h2>
          </div>
          <div className="bg-white border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-red-700 mb-2">
              <strong>Error:</strong> {error}
            </p>
            <p className="text-red-600 text-sm">
              File: {selectedFile.filename} (ID: {selectedFile.file_id})
            </p>
          </div>
          <button
            onClick={fetchData}
            className="btn-primary"
          >
            Retry Loading Data
          </button>
        </div>
      </div>
    );
  }

  const totalPages = data ? Math.ceil(Number(data.total_rows) / pageSize) : 0;

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="card">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-bill-100 rounded-lg border border-[rgba(32,30,29,0.08)]">
            <Eye className="w-5 h-5 text-bill-600" />
          </div>
          <div>
            <h2 className="text-lg lg:text-xl font-semibold text-ink truncate">{selectedFile.filename}</h2>
            <p className="text-sm lg:text-base text-sand-700 tnum">
              {data?.total_rows ? Number(data.total_rows).toLocaleString() : '0'} rows • {data?.columns?.length || selectedFile.columns?.length || '0'} columns
            </p>
          </div>
        </div>
      </div>

      <div className="card p-0 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-bill-500 mx-auto mb-4"></div>
              <p className="text-sand-700">Loading data...</p>
            </div>
          </div>
        ) : data && data.data && data.data.length > 0 ? (
          <>
            <div className="table-container">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-sand-100 sticky top-0">
                    <tr>
                      {data.columns.map((column) => (
                        <th key={column} className="px-4 py-3 text-left text-sm font-semibold text-ink min-w-[120px]">
                          <div className="flex items-center gap-2">
                            <span>{column}</span>
                            <span className="text-xs text-sand-600 bg-sand-200 px-2 py-1 rounded border border-[rgba(32,30,29,0.08)]">
                              {data.column_types?.[column] || 'unknown'}
                            </span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-sand-200">
                    {data.data.map((row, rowIndex) => (
                      <tr
                        key={rowIndex}
                        className="hover:bg-sand-100 transition-colors"
                      >
                        {data.columns.map((column) => (
                          <td key={column} className="px-4 py-3 text-sm text-sand-800 tnum">
                            {row[column] === null || row[column] === undefined ? (
                              <span className="text-sand-500 italic">null</span>
                            ) : (
                              <span className="break-words">{String(row[column])}</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="px-4 lg:px-6 py-4 bg-sand-100 border-t border-[rgba(32,30,29,0.08)] flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-xs lg:text-sm text-sand-600 tnum text-center sm:text-left">
                Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, Number(data.total_rows))} of {Number(data.total_rows).toLocaleString()} results
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                  disabled={currentPage === 0}
                  className="p-2 rounded-lg bg-white border border-[rgba(32,30,29,0.18)] text-ink disabled:opacity-50 disabled:cursor-not-allowed hover:bg-sand-100 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                <span className="px-2 lg:px-4 py-2 text-xs lg:text-sm text-sand-600 tnum">
                  Page {currentPage + 1} of {totalPages}
                </span>

                <button
                  onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                  disabled={currentPage >= totalPages - 1}
                  className="p-2 rounded-lg bg-white border border-[rgba(32,30,29,0.18)] text-ink disabled:opacity-50 disabled:cursor-not-allowed hover:bg-sand-100 transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Database className="w-12 h-12 text-sand-500 mx-auto mb-3" />
              <p className="text-sand-700">No data available</p>
              <p className="text-sand-600 text-sm mt-2">
                File: {selectedFile.filename}
              </p>
            </div>
          </div>
        )}
      </div>

      {data && (
        <div className="card">
          <h3 className="text-lg font-semibold text-ink mb-4">Column Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.columns.map((column) => (
              <div
                key={column}
                className="p-3 bg-sand-100 rounded-brand-sm border border-[rgba(32,30,29,0.08)]"
              >
                <div className="font-medium text-ink">{column}</div>
                <div className="text-sm text-sand-600">
                  Type: {data.column_types?.[column] || 'unknown'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DataViewer;
